import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { api, STATUS_LABELS, STATUS_ORDER } from "../lib/api";
import { toast } from "sonner";
import { Layers, Flame } from "lucide-react";

/**
 * Bulk update dialog — applies the same changes to multiple bath records at once.
 * Empty/untouched fields are skipped, so admins can choose to set status only, or
 * status + date, or just notes, etc.
 */
export const BulkUpdateDialog = ({ open, onOpenChange, ids = [], thirdParties = [], onDone }) => {
  const [status, setStatus] = useState("");
  const [thirdPartyId, setThirdPartyId] = useState("");
  const [date, setDate] = useState(""); // datetime-local
  const [dateField, setDateField] = useState("auto"); // auto | date_received | date_collected | date_returned | date_dispatched | expected_return_date
  const [urgent, setUrgent] = useState("noop"); // noop | on | off
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus("");
      setThirdPartyId("");
      setDate("");
      setDateField("auto");
      setUrgent("noop");
      setNote("");
    }
  }, [open]);

  // Auto-pick the natural date field for a status (only used when dateField === "auto")
  const naturalDateField = (s) => {
    switch (s) {
      case "received":
        return "date_received";
      case "collected":
        return "date_collected";
      case "returned":
        return "date_returned";
      case "dispatched":
        return "date_dispatched";
      default:
        return null;
    }
  };

  const submit = async () => {
    const payload = { ids };
    let touched = false;
    if (status) { payload.status = status; touched = true; }
    if (thirdPartyId) { payload.third_party_id = thirdPartyId; touched = true; }
    if (urgent === "on") { payload.is_urgent = true; touched = true; }
    if (urgent === "off") { payload.is_urgent = false; touched = true; }
    if (note.trim()) { payload.notes_append = note.trim(); touched = true; }
    if (date) {
      const iso = new Date(date).toISOString();
      const field = dateField === "auto" ? naturalDateField(status) : dateField;
      if (field) {
        payload[field] = iso;
        touched = true;
      }
    }
    if (!touched) {
      toast.error("Nothing to apply — set a status, date, urgent flag or a note");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/bath-records/bulk-update", payload);
      toast.success(`Updated ${r.data.updated} record${r.data.updated === 1 ? "" : "s"}`);
      onDone && onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="bulk-update-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
            <Layers className="h-5 w-5" /> Bulk update
          </DialogTitle>
          <DialogDescription>
            Apply the same change to <span className="font-semibold text-zinc-900">{ids.length}</span> selected record{ids.length === 1 ? "" : "s"}. Leave any field blank to skip it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Row label="Set status">
            <Select value={status || "none"} onValueChange={(v) => setStatus(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="bulk-status">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No change</SelectItem>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>

          <Row label="Assign third party">
            <Select value={thirdPartyId || "none"} onValueChange={(v) => setThirdPartyId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="bulk-third-party">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No change</SelectItem>
                {thirdParties.map((tp) => (
                  <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>

          <Row label="Set a date">
            <div className="flex flex-wrap gap-2">
              <Input
                type="datetime-local"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="flex-1 min-w-[180px]"
                data-testid="bulk-date"
              />
              <Select value={dateField} onValueChange={setDateField}>
                <SelectTrigger className="w-[200px]" data-testid="bulk-date-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (match status)</SelectItem>
                  <SelectItem value="date_received">Into WeLove</SelectItem>
                  <SelectItem value="date_collected">Collected by Giavani</SelectItem>
                  <SelectItem value="expected_return_date">Expected back</SelectItem>
                  <SelectItem value="date_returned">Returned</SelectItem>
                  <SelectItem value="date_dispatched">Dispatched</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Row>

          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 flex flex-wrap items-center gap-3">
            <Flame className={`h-4 w-4 ${urgent === "on" ? "text-amber-600" : "text-zinc-400"}`} />
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm font-semibold text-zinc-900">Urgent flag</div>
              <div className="text-[11px] text-zinc-500">Highlight (or clear) these records on the dashboard.</div>
            </div>
            <Select value={urgent} onValueChange={setUrgent}>
              <SelectTrigger className="w-[160px]" data-testid="bulk-urgent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="noop">No change</SelectItem>
                <SelectItem value="on">Mark urgent</SelectItem>
                <SelectItem value="off">Clear urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Row label="Append a note (added to each record's notes)">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Collected as a batch by Giavani on Tuesday morning"
              data-testid="bulk-note"
            />
          </Row>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || ids.length === 0}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="bulk-apply"
          >
            {submitting ? "Applying…" : `Apply to ${ids.length} record${ids.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
